import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { supabase } from '../../../lib/supabase';
import type { UserProfile, UserProfileRow } from '../../../types/userProfile';
import {
  shouldBackfillOnboardingFlag,
} from '../../onboarding/onboardingGate';

// ──────────────────────────────────────────────────────────────────────────────
// DB ↔ App mapping helpers
// ──────────────────────────────────────────────────────────────────────────────

function rowToProfile(row: UserProfileRow): UserProfile {
  return {
    id: row.id,
    displayName: row.display_name,
    headline: row.headline,
    currentRole: row.current_job_title,
    experienceYears: row.experience_years,
    skills: row.skills ?? [],
    industry: row.industry,
    location: row.location,
    jobSearchStatus: row.job_search_status,
    preferredJobTypes: row.preferred_job_types ?? [],
    salaryExpectationInr: row.salary_expectation_inr,
    preferredRoles: row.preferred_roles ?? [],
    noticePeriod: row.notice_period,
    holdingOffers: row.holding_offers,
    currentSalary: row.current_salary,
    salaryCurrency: row.salary_currency,
    companySizePrefs: row.company_size_prefs ?? [],
    genderIdentity: row.gender_identity,
    dateOfBirth: row.date_of_birth,
    preferredLocations: row.preferred_locations ?? [],
    linkedinUrl: row.linkedin_url,
    githubUrl: row.github_url,
    portfolioUrl: row.portfolio_url,
    // Resume data
    professionalSummary: row.professional_summary,
    experiences: row.experiences ?? [],
    education: row.education ?? [],
    projects: row.projects ?? [],
    certifications: row.certifications ?? [],
    languages: row.languages ?? [],
    resumeSource: row.resume_source,
    resumeFileUrl: row.resume_file_url,
    resumeFileName: row.resume_file_name ?? null,
    resumeRawText: row.resume_raw_text,
    resumeUpdatedAt: row.resume_updated_at,
    // App state
    onboardingCompleted: row.onboarding_completed,
    blindMode: row.blind_mode,
    tourCompleted: row.tour_completed,
    lastReviewPromptAt: row.last_review_prompt_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function patchToRow(
  userId: string,
  patch: Partial<UserProfile>,
): Partial<UserProfileRow> & { id: string } {
  const row: Partial<UserProfileRow> & { id: string } = { id: userId };

  if (patch.displayName !== undefined) row.display_name = patch.displayName;
  if (patch.headline !== undefined) row.headline = patch.headline;
  if (patch.currentRole !== undefined) row.current_job_title = patch.currentRole;
  if (patch.experienceYears !== undefined) row.experience_years = patch.experienceYears;
  if (patch.skills !== undefined) row.skills = patch.skills;
  if (patch.industry !== undefined) row.industry = patch.industry;
  if (patch.location !== undefined) row.location = patch.location;
  if (patch.jobSearchStatus !== undefined) row.job_search_status = patch.jobSearchStatus;
  if (patch.preferredJobTypes !== undefined) row.preferred_job_types = patch.preferredJobTypes;
  if (patch.salaryExpectationInr !== undefined) row.salary_expectation_inr = patch.salaryExpectationInr;
  if (patch.preferredRoles !== undefined) row.preferred_roles = patch.preferredRoles;
  if (patch.noticePeriod !== undefined) row.notice_period = patch.noticePeriod;
  if (patch.holdingOffers !== undefined) row.holding_offers = patch.holdingOffers;
  if (patch.currentSalary !== undefined) row.current_salary = patch.currentSalary;
  if (patch.salaryCurrency !== undefined) row.salary_currency = patch.salaryCurrency;
  if (patch.companySizePrefs !== undefined) row.company_size_prefs = patch.companySizePrefs;
  if (patch.genderIdentity !== undefined) row.gender_identity = patch.genderIdentity;
  if (patch.dateOfBirth !== undefined) row.date_of_birth = patch.dateOfBirth;
  if (patch.preferredLocations !== undefined) row.preferred_locations = patch.preferredLocations;
  if (patch.linkedinUrl !== undefined) row.linkedin_url = patch.linkedinUrl;
  if (patch.githubUrl !== undefined) row.github_url = patch.githubUrl;
  if (patch.portfolioUrl !== undefined) row.portfolio_url = patch.portfolioUrl;
  // Resume data
  if (patch.professionalSummary !== undefined) row.professional_summary = patch.professionalSummary;
  if (patch.experiences !== undefined) row.experiences = patch.experiences;
  if (patch.education !== undefined) row.education = patch.education;
  if (patch.projects !== undefined) row.projects = patch.projects;
  if (patch.certifications !== undefined) row.certifications = patch.certifications;
  if (patch.languages !== undefined) row.languages = patch.languages;
  if (patch.resumeSource !== undefined) row.resume_source = patch.resumeSource;
  if (patch.resumeFileUrl !== undefined) row.resume_file_url = patch.resumeFileUrl;
  // resume_file_name column not in DB schema — stored only in local draft, not persisted
  if (patch.resumeRawText !== undefined) row.resume_raw_text = patch.resumeRawText;
  if (patch.resumeUpdatedAt !== undefined) row.resume_updated_at = patch.resumeUpdatedAt;
  // App state
  if (patch.onboardingCompleted !== undefined) row.onboarding_completed = patch.onboardingCompleted;
  if (patch.blindMode !== undefined) row.blind_mode = patch.blindMode;
  if (patch.tourCompleted !== undefined) row.tour_completed = patch.tourCompleted;
  if (patch.lastReviewPromptAt !== undefined) row.last_review_prompt_at = patch.lastReviewPromptAt;

  return row;
}

// ──────────────────────────────────────────────────────────────────────────────
// Context — one shared profile for the whole app (onboarding + gates + shell).
// Without this, OnboardingWizard.save() updated only that component's copy of
// state, while AppStateManager still saw onboarding_completed=false and sent
// users back to /onboarding after "completion".
// ──────────────────────────────────────────────────────────────────────────────

export interface UseUserProfileResult {
  profile: UserProfile | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  save: (patch: Partial<UserProfile>) => Promise<void>;
  refresh: () => Promise<void>;
}

const UserProfileContext = createContext<UseUserProfileResult | null>(null);

export function UserProfileProvider({ children }: { children: React.ReactNode }) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        setProfile(null);
        return;
      }

      const { data, error: qErr } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', session.user.id)
        .maybeSingle();

      if (qErr) throw qErr;

      const nextProfile = data ? rowToProfile(data as UserProfileRow) : null;
      setProfile(nextProfile);

      // Legacy users: profile populated before onboarding_completed flag — backfill once.
      if (nextProfile && shouldBackfillOnboardingFlag(nextProfile)) {
        void supabase
          .from('user_profiles')
          .update({ onboarding_completed: true })
          .eq('id', nextProfile.id)
          .then(({ error: backfillErr }) => {
            if (backfillErr) {
              console.warn('[useUserProfile] onboarding_completed backfill failed:', backfillErr.message);
              return;
            }
            setProfile((prev) =>
              prev ? { ...prev, onboardingCompleted: true } : prev,
            );
          });
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not load profile.';
      setError(msg);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      void fetchProfile();
    };
    run();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        setProfile(null);
        setLoading(false);
        return;
      }
      // Block onboarding gates from seeing stale profile=null + loading=false
      // for one frame after SIGNED_IN (before fetchProfile's first line runs).
      setLoading(true);
      run();
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const refresh = useCallback(async (): Promise<void> => {
    await fetchProfile();
  }, [fetchProfile]);

  const save = useCallback(
    async (patch: Partial<UserProfile>): Promise<void> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('You must be signed in to save your profile.');
        throw new Error('Not authenticated');
      }

      setSaving(true);
      setError(null);

      try {
        const rowPatch = patchToRow(session.user.id, patch);

        const { data, error: upErr } = await supabase
          .from('user_profiles')
          .upsert(rowPatch, { onConflict: 'id' })
          .select('*')
          .maybeSingle();

        if (upErr) throw upErr;

        if (data) {
          setProfile(rowToProfile(data as UserProfileRow));
        } else {
          await fetchProfile();
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Could not save profile.';
        setError(msg);
        throw e instanceof Error ? e : new Error(msg);
      } finally {
        setSaving(false);
      }
    },
    [fetchProfile],
  );

  const value = useMemo(
    () => ({ profile, isLoading, isSaving, error, save, refresh }),
    [profile, isLoading, isSaving, error, save, refresh],
  );

  return <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>;
}

/**
 * Current user's `user_profiles` row — must run under {@link UserProfileProvider}.
 */
export function useUserProfile(): UseUserProfileResult {
  const ctx = useContext(UserProfileContext);
  if (!ctx) {
    throw new Error('useUserProfile must be used within UserProfileProvider');
  }
  return ctx;
}
